import { Injectable } from '@angular/core'
import { IAttribute, INode, ITag, IText, parse, SyntaxKind, walk } from 'html5parser'

@Injectable({ providedIn: 'root' })
export class CmsDOMInputParser {

  constructor () {
  }

  parse (text: string): string {
    return this.processSyntaxTree(parse(text))
  }

  private processSyntaxTree (tree: INode[]): string {
    const result: string[] = []
    walk(tree, {
      enter: (node) => {
        if (node.type === SyntaxKind.Text && !node.value.startsWith('{')) {
          result.push(this.replaceEncodedHtmlEntities(node?.value ?? ''))
        }
        if (node.type === SyntaxKind.Tag) {
          result.push(this.parseTag(node as ITag, false))
        }
      },
      leave: (node) => {
        if (node.type === SyntaxKind.Tag && node.name !== 'br') {
          result.push(this.parseTag(node as ITag, true))
        }
      }
    })
    return result.join('')
  }

  parseTag (node: ITag, close: boolean): string {
    node = node as ITag
    if (node.attributes.findIndex(attribute => attribute.name?.value === 'data-fs-style') > -1) {
      if (node.name === 'div') {
        return this.getDivStyleElement(node, close)
      } else if (node.name === 'ul') {
        return this.getListElement(node, close)
      } else if (node.name === 'li') {
        return this.getListItemElement(node, close)
      } else if (node.name === 'table') {
        return this.getTableElement(node, close)
      } else if (node.name === 'td') {
        return this.getTableCellElement(node, close)
      } else {
        console.warn('WARN -> DOMParser -> Unknown style tag', node)
        return ''
      }
    } else if (node.attributes.findIndex(attribute => attribute.name?.value === 'data-fs-type') > -1) {
      return this.getLinkElement(node, close)
    } else if (node.name === 'script') {
      return ''
    } else {
      if (node.name === 'a') {
        return ''
      }
      return close
        ? `</${node.name ?? 'empty'}>`
        : `<${node.name ?? 'empty'}>`
    }
  }

  getLinkElement (node: ITag, close: boolean): string {
    if (close) {
      return '</a>'
    }
    const type = node.attributes.find(attribute => attribute.name?.value === 'data-fs-type')?.value?.value ?? ''
    const linkType = type.substring(type.indexOf('.') + 1)
    const script = node.body?.find((n: INode) => (n as ITag).name === 'script') as ITag
    const scriptData = (script?.body?.[0] as IText)?.value ?? '{}'
    const linkData = JSON.parse(scriptData)
    return this.createLinkElement(linkData, linkType)
  }

  createLinkElement (linkData: any, linkType: string): string {
    const anchor = linkData?.lt_anchor?.value ?? ''
    const url = linkData?.lt_url?.value ?? ''
    const target = linkData?.lt_target?.value?.identifier ?? ''
    const tooltip = linkData?.lt_tooltip_text?.value ?? ''
    const classes = linkData.lt_linkstyle?.value?.identifier === 'button' ? ' class="btn btn-lg btn-primary"' : ''
    if (linkType === 'url') {
      const fullUrl = `${url}${anchor.length ? '#' + anchor : ''}`
      const noFollow = url.indexOf('http') > -1 || url.indexOf('www') > -1 || url.indexOf('') ? ' rel="nofollow"' : ''
      // eslint-disable-next-line max-len
      return `<a href="${fullUrl}" data-cy="url-link" class="cms-url-link" target="${target}" title="${tooltip}"${classes}${noFollow}>`
    } else if (linkType === 'content_link') {
      const page = linkData?.lt_page?.value?.identifier ?? ''
      // eslint-disable-next-line max-len
      return `<a href="" data-cy="content-link" class="cms-content-link" target="${target}" title="${tooltip}" data-reference="${page}"${classes}>`
    } else if (linkType === 'anchor') {
      // eslint-disable-next-line max-len
      return `<a data-cy="anchor-link" href="#${anchor}" class="cms-anchor-link" title="${tooltip}" data-anchor="${anchor}" ${classes}>`
    } else if (linkType === 'product_link') {
      const product = linkData?.lt_product?.value?.[0]?.identifier ?? ''
      const productCode = product.substring(product.indexOf('/') + 1)
      // eslint-disable-next-line max-len
      return `<a href="" data-cy="product-link" class="cms-product-link" target="${target}" title="${tooltip}" data-product-code="${productCode}">`
    } else if (linkType === 'document_link') {
      const pdf = linkData?.lt_reference?.value?.[0]?.value?.target ?? {}
      const assetType = pdf.assetType ?? ''
      const baseUrl = pdf.baseUrl ?? ''
      const filename = pdf.title ?? ''
      // eslint-disable-next-line max-len
      return `<a href="${baseUrl}" data-cy="document-link" class="cms-document-link" data-assettype="${assetType}" data-filename="${filename}" target="${target}" title="${tooltip}">`
    } else if (linkType === 'email_link') {
      return `<a href="mailto:${url}" data-cy="email-link" class="cms-email-link" title="${tooltip}">`
    } else {
      return '<a>'
    }
  }

  getListItemElement (node: ITag, close: boolean): string {
    if (close) {
      return '</li>'
    }
    const styleAttribute = node.attributes.find((attribute: IAttribute) => attribute.name?.value === 'data-fs-style')
    let styleTag = styleAttribute?.value?.value
    styleTag = (styleTag !== '-' && styleTag !== '0') ? `${styleTag}.` : styleTag
    return styleAttribute
      ? `<li style="list-style-type: '${styleTag} ';">`
      : '<li>'
  }

  getListElement (node: ITag, close: boolean): string {
    if (close) {
      return '</ul>'
    }
    return node.attributes
      .find((attribute: IAttribute) => attribute.name?.value === 'data-fs-style')
      ?.value?.value === '1'
      ? '<ul style="list-style-type: initial;">'
      : '<ul>'
  }

  getDivStyleElement (node: ITag, close: boolean): string {
    const styleAttribute = node.attributes.find(attribute => attribute?.name?.value === 'data-fs-style')
    const styleValue = styleAttribute?.value?.value ?? ''
    if (close) {
      return styleValue.indexOf('add_style') > -1
        ? '</empty>'
        : `</${styleValue.substring(styleValue.indexOf('.') + 1)}>`
    }
    return styleValue.indexOf('add_style') > -1
      ? `<empty class="${styleValue.substring(styleValue.indexOf('add_style_') + 10)}">`
      : `<${styleValue.substring(styleValue.indexOf('.') + 1)}>`
  }

  getTableCellElement (node: ITag, close: boolean): string {
    if (close) {
      return '</td>'
    }
    const styles = node.attributes.find(attribute => attribute.name?.value === 'style')?.value?.value ?? ''
    const rowSpan = node.attributes.find(attribute => attribute.name?.value === 'rowspan')?.value?.value ?? '1'
    const colSpan = node.attributes.find(attribute => attribute.name?.value === 'colspan')?.value?.value ?? '1'
    return `<td ${styles?.length ? `style="${styles}"` : ''} rowspan="${rowSpan}" colspan="${colSpan}">`
  }

  getTableElement (node: ITag, close: boolean): string {
    if (close) {
      return '</table>'
    }
    let borderColor = node.body
      ?.flatMap((n: any) => n?.body ?? [])
      ?.flatMap((n: ITag) => n.attributes ?? [])
      ?.find(s => (s?.value?.value?.indexOf('background-color') ?? -1) > -1)
      ?.value?.value ?? ''
    borderColor = borderColor.substring(borderColor.indexOf('background-color') + 17)
    const style = borderColor?.length ? `style="border-color: ${borderColor}"` : ''
    return `<table class="table table-striped" ${style}>`
  }

  replaceEncodedHtmlEntities (input: string): string {
    const entities: { [key: string]: string } = {
      '&lt;': '<',
      '&gt;': '>',
    }

    return input.replace(/&(lt|gt);/g, (match) => entities[match] || match)
  }
}
